import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Calendar, DollarSign, CheckCircle, XCircle, Shield, TrendingUp, Bell, Send } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      toast.error('Acceso solo para administradores');
      navigate('/');
      return;
    }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    try {
      const config = { withCredentials: true };

      const [statsRes, pendingRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, config),
        axios.get(`${API}/admin/events/pending`, config),
        axios.get(`${API}/admin/users`, config)
      ]);

      setStats(statsRes.data);
      setPendingEvents(pendingRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (eventId) => {
    try {
      await axios.put(`${API}/admin/events/${eventId}/approve`, {}, {
        withCredentials: true
      });
      toast.success('Evento aprobado y publicado');
      loadData();
    } catch (error) {
      toast.error('Error al aprobar evento');
    }
  };

  const handleReject = async (eventId) => {
    try {
      await axios.put(`${API}/admin/events/${eventId}/reject`, {}, {
        withCredentials: true
      });
      toast.success('Evento rechazado, volvió a borrador');
      loadData();
    } catch (error) {
      toast.error('Error al rechazar evento');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, {
        withCredentials: true
      });
      toast.success(`Rol actualizado a ${newRole}`);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar rol');
    }
  };

  if (!user || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8" data-testid="admin-panel">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-10 h-10 text-primary" />
          <div>
            <h1 className="text-4xl font-heading font-bold text-[#0A0A0A]">Panel Admin</h1>
            <p className="text-[#737373]">Gestión global de la plataforma</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-white/50 border-[#E5E5E5]">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#737373]">Usuarios</p>
                    <p className="text-3xl font-bold text-[#0A0A0A]" data-testid="stat-users">{stats.total_users}</p>
                  </div>
                  <Users className="w-8 h-8 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/50 border-[#E5E5E5]">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#737373]">Eventos</p>
                    <p className="text-3xl font-bold text-[#0A0A0A]" data-testid="stat-events">{stats.total_events}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/50 border-[#E5E5E5]">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#737373]">RSVPs</p>
                    <p className="text-3xl font-bold text-[#0A0A0A]" data-testid="stat-rsvps">{stats.total_rsvps}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/50 border-[#E5E5E5]">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#737373]">Ingresos</p>
                    <p className="text-2xl font-bold text-[#0A0A0A]" data-testid="stat-revenue">
                      ${stats.commission_revenue.toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-primary" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="events" className="w-full">
          <TabsList className="bg-white border-[#E5E5E5]">
            <TabsTrigger value="events" data-testid="tab-events">
              Moderación ({pendingEvents.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              Usuarios ({users.length})
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <Bell className="w-4 h-4 mr-1" /> Notificaciones
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events">
            <Card className="bg-white/50 border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-[#0A0A0A]">Eventos pendientes de revisión</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingEvents.length === 0 ? (
                  <p className="text-center text-[#737373] py-8" data-testid="no-pending-events">
                    No hay eventos pendientes de revisión
                  </p>
                ) : (
                  <div className="space-y-4">
                    {pendingEvents.map(event => (
                      <div key={event.event_id} className="bg-white/50 border border-[#E5E5E5] rounded-lg p-4" data-testid={`pending-${event.event_id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">{event.category}</span>
                              <span className="text-xs text-[#737373]">por {event.organizer_name}</span>
                            </div>
                            <h3 className="text-lg font-semibold text-[#0A0A0A] mb-1">{event.title}</h3>
                            <p className="text-sm text-[#737373]">{event.description_short}</p>
                            <p className="text-xs text-[#737373] mt-2">
                              {format(new Date(event.start_date), "d 'de' MMMM, yyyy 'a las' HH:mm", { locale: es })}
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              onClick={() => handleApprove(event.event_id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-${event.event_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Aprobar
                            </Button>
                            <Button
                              onClick={() => handleReject(event.event_id)}
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              data-testid={`reject-${event.event_id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Rechazar
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="bg-white/50 border-[#E5E5E5]">
              <CardHeader>
                <CardTitle className="text-[#0A0A0A]">Gestión de usuarios</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {users.map(u => (
                    <div key={u.user_id} className="flex items-center justify-between p-3 bg-white/50 border border-[#E5E5E5] rounded-lg" data-testid={`user-${u.user_id}`}>
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-semibold">
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-[#0A0A0A]">{u.name}</p>
                          <p className="text-xs text-[#737373]">{u.email}</p>
                        </div>
                      </div>
                      <Select
                        value={u.role}
                        onValueChange={(value) => handleRoleChange(u.user_id, value)}
                      >
                        <SelectTrigger className="w-36 bg-white border-[#E5E5E5] text-[#0A0A0A]" data-testid={`role-select-${u.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#E5E5E5]">
                          <SelectItem value="user">Usuario</SelectItem>
                          <SelectItem value="organizer">Organizador</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications">
            <NotificationSender />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const NotificationSender = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Título y mensaje son requeridos');
      return;
    }
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/notifications/send`,
        { title, body, url },
        {
          withCredentials: true
        }
      );
      setLastResult(response.data);
      toast.success(`Enviadas: ${response.data.sent} / ${response.data.total}`);
      setTitle('');
      setBody('');
    } catch (error) {
      toast.error('Error al enviar notificación');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="bg-white/50 border-[#E5E5E5]" data-testid="notification-sender">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#0A0A0A]">
          <Bell className="w-6 h-6 text-primary" />
          Enviar Notificación Push (Broadcast)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-[#737373]">
          Envía una notificación push a todos los usuarios suscritos
        </p>

        <div>
          <Label className="text-[#0A0A0A]">Título *</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={80}
            placeholder="Ej: Nuevo evento esta semana!"
            className="bg-white border-[#E5E5E5] text-[#0A0A0A]"
            data-testid="notification-title-input"
          />
        </div>

        <div>
          <Label className="text-[#0A0A0A]">Mensaje *</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={200}
            rows={3}
            placeholder="Ej: Maratón Solidaria este sábado, no te lo pierdas"
            className="bg-white border-[#E5E5E5] text-[#0A0A0A]"
            data-testid="notification-body-input"
          />
        </div>

        <div>
          <Label className="text-[#0A0A0A]">URL al hacer clic</Label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/"
            className="bg-white border-[#E5E5E5] text-[#0A0A0A]"
            data-testid="notification-url-input"
          />
        </div>

        <Button
          onClick={handleSend}
          disabled={sending || !title || !body}
          className="bg-primary hover:bg-primary/90 text-white"
          data-testid="send-notification-button"
        >
          <Send className="w-4 h-4 mr-2" />
          {sending ? 'Enviando...' : 'Enviar a todos'}
        </Button>

        {lastResult && (
          <div className="p-4 bg-white/50 rounded-lg border border-[#E5E5E5]">
            <p className="text-sm text-[#525252]">
              Última envío: <span className="text-primary font-bold">{lastResult.sent}</span> entregadas /
              <span className="text-red-400 font-bold ml-1">{lastResult.failed}</span> fallidas /
              <span className="text-[#737373] ml-1">{lastResult.total}</span> suscriptos
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminPanel;
